import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  Banner,
  Badge,
  Divider,
  Box,
  ProgressBar,
  Modal,
  Icon,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import { CheckCircleIcon } from '@shopify/polaris-icons';
import { useShopifyFetch } from '../hooks/useShopifyFetch';
import { overallQuotaLevel, quotaLevelFromFlags } from '../utils/quota';

interface Plan {
  id: string;
  name: string;
  price: number;
  price_display: string;
  trial_days: number;
  features: string[];
  conversion_limit: number;
  affiliate_limit: number;
  popular: boolean;
}

interface SubscriptionStatus {
  plan: string;
  status: string;
  features: string[];
  conversion_limit: number;
  affiliate_limit: number;
  current_period_end: string | null;
}

interface Usage {
  plan: string;
  usage: {
    conversions: {
      used: number;
      limit: number;
      remaining: number;
      percentage: number;
    };
    affiliates: {
      used: number;
      limit: number;
      remaining: number;
      percentage: number;
    };
  };
  overage?: {
    /** True when conversions this period have passed the plan's included limit. */
    in_overage: boolean;
    /** True when the plan bills the overage (usage plans) vs hard-caps it (Free). */
    billed: boolean;
    /** Per-period cap on overage charges, in dollars. */
    usage_capped_amount: number;
  };
}

export default function Billing() {
  const { get, post } = useShopifyFetch();
  const queryClient = useQueryClient();

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Break out of the embedded App Bridge iframe to Shopify's charge-approval
  // page. Assigning `window.top.location.href` throws a cross-origin
  // SecurityError inside the iframe (the top frame is admin.shopify.com), so the
  // approval page never opened. A top-targeted anchor click IS permitted for the
  // top frame and is App Bridge's documented redirect fallback.
  const redirectToApproval = useCallback((url: string) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_top';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }, []);

  // Fetch plans
  const { data: plansData, isLoading: plansLoading } = useQuery<{ plans: Plan[] }>({
    queryKey: ['billing-plans'],
    queryFn: () => get('/api/billing/plans'),
  });

  // Fetch current subscription
  const { data: subscription, isLoading: subscriptionLoading } = useQuery<SubscriptionStatus>({
    queryKey: ['billing-status'],
    queryFn: () => get('/api/billing/status'),
  });

  // Fetch usage
  const { data: usage, isLoading: usageLoading } = useQuery<Usage>({
    queryKey: ['billing-usage'],
    queryFn: () => get('/api/billing/usage'),
  });

  // Subscribe mutation
  const subscribeMutation = useMutation({
    mutationFn: (plan: string) => post<{ confirmation_url?: string }>('/api/billing/subscribe', { plan }),
    onSuccess: (data) => {
      if (data?.confirmation_url) {
        // Redirect to Shopify payment approval (top-frame navigation).
        redirectToApproval(data.confirmation_url);
      } else {
        // Plan activated immediately (free plan)
        queryClient.invalidateQueries({ queryKey: ['billing-status'] });
        queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
        setUpgradeModalOpen(false);
      }
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => post('/api/billing/cancel'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-status'] });
      queryClient.invalidateQueries({ queryKey: ['billing-usage'] });
      setCancelModalOpen(false);
    },
  });

  const handleSelectPlan = useCallback((plan: Plan) => {
    setSelectedPlan(plan);
    setUpgradeModalOpen(true);
  }, []);

  const handleConfirmUpgrade = useCallback(() => {
    if (selectedPlan) {
      subscribeMutation.mutate(selectedPlan.id);
    }
  }, [selectedPlan, subscribeMutation]);

  const isLoading = plansLoading || subscriptionLoading || usageLoading;

  if (isLoading) {
    return (
      <SkeletonPage title="Billing">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
          </Layout.Section>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={4} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  const plans = plansData?.plans || [];
  const currentPlan = subscription?.plan || 'free';
  const isFreePlan = currentPlan === 'free';

  // Persistent quota warning: derived from per-metric usage and any
  // over_plan_quota_* flags the backend returns on success responses.
  const usageQuotaLevel = overallQuotaLevel(usage?.usage);
  const flagQuotaLevel = quotaLevelFromFlags({
    ...(usage as unknown as Record<string, unknown>),
    ...(subscription as unknown as Record<string, unknown>),
  });
  const quotaLevel = flagQuotaLevel === 'over' ? 'over' : usageQuotaLevel;

  // Billed overage: on usage plans, conversions past the included limit are
  // charged (up to a monthly cap) rather than blocked. Surface that explicitly
  // so a real charge is never a surprise — this supersedes the generic "over
  // your limit / blocked" message, which only applies to hard-capped plans.
  const overage = usage?.overage;
  const inBilledOverage = !!overage?.in_overage && !!overage?.billed;

  return (
    <Page title="Billing" subtitle="Manage your subscription and usage">
      <Layout>
        {/* Billed-overage notice (usage plans) */}
        {inBilledOverage && (
          <Layout.Section>
            <Banner tone="warning" title="You've entered usage overage">
              <p>
                {`You've used all the conversions included in your plan. Additional conversions this month are billed as overage${
                  overage && overage.usage_capped_amount > 0
                    ? `, up to $${overage.usage_capped_amount.toLocaleString()}`
                    : ''
                }. Upgrade to raise your included limit and lower your per-conversion cost.`}
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Persistent plan-limit warning (hard-capped plans / approaching) */}
        {!inBilledOverage && quotaLevel !== 'ok' && (
          <Layout.Section>
            <Banner
              tone="warning"
              title={
                quotaLevel === 'over'
                  ? "You're over your plan limit"
                  : "You're approaching your plan limit"
              }
            >
              <p>
                {quotaLevel === 'over'
                  ? "You've reached your plan's limit. New conversions and affiliates are blocked until you upgrade."
                  : "You're close to your plan's limit. Upgrade now to avoid interruptions to conversion and affiliate tracking."}
              </p>
            </Banner>
          </Layout.Section>
        )}

        {/* Current Plan Overview */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">
                    Current Plan
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    Your current subscription status
                  </Text>
                </BlockStack>
                <Badge tone={isFreePlan ? 'attention' : 'success'}>
                  {plans.find(p => p.id === currentPlan)?.name || 'Free'}
                </Badge>
              </InlineStack>

              <Divider />

              {!isFreePlan && subscription?.current_period_end && (
                <Text variant="bodySm" tone="subdued" as="p">
                  Next billing date: {new Date(subscription.current_period_end).toLocaleDateString()}
                </Text>
              )}

              {isFreePlan && (
                <Banner tone="info">
                  <p>
                    You're on the Free plan. Upgrade to unlock more conversions, affiliates, and features.
                  </p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Usage Stats */}
        {usage && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Usage This Month
                </Text>

                <BlockStack gap="300">
                  {/* Conversions */}
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text variant="bodyMd" as="span">
                        Conversions
                      </Text>
                      <Text variant="bodyMd" as="span">
                        {usage.usage.conversions.limit === -1
                          ? `${usage.usage.conversions.used} / Unlimited`
                          : `${usage.usage.conversions.used} / ${usage.usage.conversions.limit}`}
                      </Text>
                    </InlineStack>
                    {usage.usage.conversions.limit !== -1 && (
                      <ProgressBar
                        progress={usage.usage.conversions.percentage}
                        tone={usage.usage.conversions.percentage > 80 ? 'critical' : 'primary'}
                      />
                    )}
                  </BlockStack>

                  {/* Affiliates */}
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text variant="bodyMd" as="span">
                        Active Affiliates
                      </Text>
                      <Text variant="bodyMd" as="span">
                        {usage.usage.affiliates.limit === -1
                          ? `${usage.usage.affiliates.used} / Unlimited`
                          : `${usage.usage.affiliates.used} / ${usage.usage.affiliates.limit}`}
                      </Text>
                    </InlineStack>
                    {usage.usage.affiliates.limit !== -1 && (
                      <ProgressBar
                        progress={usage.usage.affiliates.percentage}
                        tone={usage.usage.affiliates.percentage > 80 ? 'critical' : 'primary'}
                      />
                    )}
                  </BlockStack>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Available Plans */}
        <Layout.Section>
          <Text variant="headingMd" as="h2">
            Available Plans
          </Text>
        </Layout.Section>

        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            {plans.map((plan) => (
                <Card key={plan.id}>
                  <BlockStack gap="400">
                    {/* Plan Header */}
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text variant="headingMd" as="h3">
                          {plan.name}
                        </Text>
                        {plan.popular && (
                          <Badge tone="info">
                            Popular
                          </Badge>
                        )}
                      </InlineStack>
                      <Text variant="headingLg" as="p">
                        {plan.price_display}
                      </Text>
                      {plan.trial_days > 0 && (
                        <Text variant="bodySm" tone="success" as="p">
                          {plan.trial_days}-day free trial
                        </Text>
                      )}
                    </BlockStack>

                    <Divider />

                    {/* Features */}
                    <BlockStack gap="200">
                      {plan.features.map((feature, idx) => (
                        <InlineStack key={idx} gap="200" blockAlign="start">
                          <Box>
                            <Icon source={CheckCircleIcon} tone="success" />
                          </Box>
                          <Text variant="bodySm" as="span">
                            {feature}
                          </Text>
                        </InlineStack>
                      ))}
                    </BlockStack>

                    {/* Action Button */}
                    <Box paddingBlockStart="200">
                      {currentPlan === plan.id ? (
                        <Button disabled fullWidth>
                          Current Plan
                        </Button>
                      ) : (
                        <Button
                          variant={plan.popular ? 'primary' : 'secondary'}
                          fullWidth
                          onClick={() => handleSelectPlan(plan)}
                        >
                          {plan.price === 0 ? 'Downgrade' : 'Upgrade'}
                        </Button>
                      )}
                    </Box>
                  </BlockStack>
                </Card>
            ))}
          </InlineGrid>
        </Layout.Section>

        {/* Cancel Subscription */}
        {!isFreePlan && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  Cancel Subscription
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  If you cancel, you'll be downgraded to the Free plan at the end of your billing period.
                </Text>
                <Button
                  tone="critical"
                  onClick={() => setCancelModalOpen(true)}
                >
                  Cancel Subscription
                </Button>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>

      {/* Upgrade Confirmation Modal */}
      <Modal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        title={`${selectedPlan?.price === 0 ? 'Downgrade' : 'Upgrade'} to ${selectedPlan?.name}`}
        primaryAction={{
          content: selectedPlan?.price === 0 ? 'Confirm Downgrade' : 'Continue to Payment',
          onAction: handleConfirmUpgrade,
          loading: subscribeMutation.isPending,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setUpgradeModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {selectedPlan?.price === 0 ? (
              <Banner tone="warning">
                <p>
                  Downgrading to the Free plan will:
                </p>
                <ul>
                  <li>Reduce your conversion limit to {selectedPlan.conversion_limit}/month</li>
                  <li>Limit affiliates to {selectedPlan.affiliate_limit}</li>
                  <li>Remove access to advanced features</li>
                </ul>
              </Banner>
            ) : (
              <BlockStack gap="300">
                <Text variant="bodyMd" as="p">
                  You're upgrading to the <strong>{selectedPlan?.name}</strong> plan.
                </Text>
                {selectedPlan?.trial_days && selectedPlan.trial_days > 0 && (
                  <Banner tone="success">
                    <p>
                      You'll get a {selectedPlan.trial_days}-day free trial. You won't be charged until the trial ends.
                    </p>
                  </Banner>
                )}
                <Text variant="bodyMd" as="p">
                  After clicking "Continue to Payment", you'll be redirected to Shopify to approve the charge.
                </Text>
              </BlockStack>
            )}

            {subscribeMutation.isError && (
              <Banner tone="critical">
                <p>Failed to process subscription. Please try again.</p>
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Cancel Confirmation Modal */}
      <Modal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Subscription"
        primaryAction={{
          content: 'Yes, Cancel',
          onAction: () => cancelMutation.mutate(),
          loading: cancelMutation.isPending,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Keep Subscription',
            onAction: () => setCancelModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Banner tone="warning">
            <p>
              Are you sure you want to cancel your subscription? You'll lose access to:
            </p>
            <ul>
              {subscription?.features.map((feature, idx) => (
                <li key={idx}>{feature}</li>
              ))}
            </ul>
            <p>
              Your current plan will remain active until the end of your billing period.
            </p>
          </Banner>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
